// Breakpoints Constant
export const SMALL = 'SMALL';
export const MEDIUM = 'MEDIUM';
export const LARGE = 'LARGE';
export const XLARGE = 'XLARGE';

/**
 * Get viewport width of current device
 */
export const getDeviceType = () => {
  const breakpoints = {
    medium: 679,
    large: 1000,
    xlarge: 1296,
  };

  const isClient = typeof window !== 'undefined';
  const viewPortWidth = isClient ? window.innerWidth : -1;

  if (viewPortWidth === -1) {
    return SMALL;
  }

  if (viewPortWidth < breakpoints.medium) {
    return SMALL;
  }

  if (viewPortWidth < breakpoints.large && viewPortWidth >= breakpoints.medium) {
    return MEDIUM;
  }

  if (viewPortWidth < breakpoints.xlarge && viewPortWidth >= breakpoints.large) {
    return LARGE;
  }

  return XLARGE;
};

export const isMobile = () => {
  const breakpoint = getDeviceType();

  return breakpoint === SMALL;
};

/**
 * @param {HTMLElement} element
 * @param {string} types One or more space-separated event types, such as 'click', 'click click.gds.tab'
 * @param {object} data Any data passed when initializing the event.
 */
export const dispatchEvent = (element, types, data = null, eventProps = {}) => {
  const whitespace = /[^\x20\t\r\n\f]+/g;
  const eventTypes = (types || '').match(whitespace) || [''];
  const props = {
    bubbles: true,
    cancelable: true,
    composed: true,
    ...eventProps,
  };

  eventTypes.forEach(type => {
    const event = new CustomEvent(type, {
      detail: data,
      ...props,
    });
    element.dispatchEvent(event);
  });
};

/**
 * get element position
 */
export function GetDimensions(elements) {
  const elem = elements.length ? elements[0] : elements;

  if (elem === window || elem === document) {
    throw new Error("I'm sorry, I'm afraid I can't do that.");
  }

  const rect = elem.getBoundingClientRect();
  const winWidth = document.documentElement.clientWidth;
  const winHeight = document.documentElement.clientHeight;
  const parRect = elem.parentNode.getBoundingClientRect();
  const winY = window.pageYOffset;
  const winX = window.pageXOffset;

  return {
    width: rect.width,
    height: rect.height,
    offset: {
      top: rect.top + winY,
      left: rect.left + winX,
    },
    parentDims: {
      width: parRect.width,
      height: parRect.height,
      offset: {
        top: parRect.top + winY,
        left: parRect.left + winX,
      },
    },
    windowDims: {
      width: winWidth,
      height: winHeight,
      offset: {
        top: winY,
        left: winX,
      },
    },
  };
}

/**
 * detect if an element is outside of its container's width
 */
export function horizontallyBound(parentDiv, childDiv) {
  const parentRect = parentDiv.getBoundingClientRect();
  const childRect = childDiv.getBoundingClientRect();

  return parentRect.left > childRect.left || parentRect.right < childRect.right;
}

/**
 * event target
 */
export function getEventTarget(event) {
  try {
    if (typeof event.composedPath === 'function') {
      const path = event.composedPath();
      return path[0];
    }
    return event.target;
  } catch (error) {
    /* eslint-disable no-console */
    console.log(error);
    return event.target;
  }
}

export function createElement(tag, className, content) {
  const e = window.document.createElement(tag);
  const eleClassName = className || '';
  const eleContent = content || '';
  e.className = eleClassName;
  if (eleContent !== undefined) {
    e.textContent = eleContent;
  }

  return e;
}

export const toArray = arrayLike => Array.prototype.slice.call(arrayLike);

export const parseDateFormat = dateFormat => {
  const format = ['d', 'm', 'Y'];
  const frm = [];
  const str = [];
  dateFormat.split('').forEach(cur => {
    if (format.indexOf(cur) >= 0) {
      frm.push(cur);
    } else {
      str.push(cur);
    }
  });

  return { delimiter: str.splice(0, str.length / 2).join(''), datePattern: frm };
};

export const getISODateString = date =>
  new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()))
    .toISOString()
    .split('T')[0];

export const parseISODateString = s => {
  const utcDate = new Date(Date.parse(s));
  return new Date(utcDate.getFullYear(), utcDate.getMonth(), utcDate.getDate());
};

/*
element: HTML node element on which effect needs to be applied
fraction: 0 to 1 floating number. Slower the fraction, slower the animation.
*/

export const fadeOut = (element, fraction = 0.05) => {
  let opacity = 1;

  const decrease = () => {
    opacity -= fraction;
    if (opacity <= 0) {
      // complete
      element.style.opacity = 0;
      return true;
    }
    element.style.opacity = opacity;
    return requestAnimationFrame(decrease);
  };
  decrease();
};

export const fadeIn = (element, fraction) => {
  let opacity = 0;

  const increase = () => {
    opacity += fraction;
    if (opacity >= 1) {
      // complete
      element.style.opacity = 1;
      return true;
    }
    element.style.opacity = opacity;
    return requestAnimationFrame(increase);
  };
  increase();
};

if (!window.requestAnimationFrame) {
  window.requestAnimationFrame = fn => {
    setTimeout(fn, 16.66);
  };
}

export const slideUp = (element, height = 0, time = 500) => {
  element.style.transition = `all ${time}ms ease-in-out`;
  element.style.height = `${height}px`;
};

export const slideDown = (element, height = 0, time = 500) => {
  element.style.transition = `all ${time}ms ease-in-out`;
  element.style.height = `${height || element.scrollHeight}px`;
};

export const isNumber = value => {
  try {
    return Number(value) === value;
  } catch {
    return false;
  }
};

export const isDate = value => {
  return Object.prototype.toString.call(value) === '[object Date]';
};

export const isFunction = value => {
  return !!(value && value.constructor && value.call && value.apply);
};

export const isEmpty = value => {
  if (value === null || value === undefined) return true;
  if (isNumber(value)) return value === 0;
  if (isDate(value)) return isNaN(value.getTime());
  if (isFunction(value)) return false;
  const length = value.length;
  if (isNumber(length)) return length === 0;
  const size = value.size;
  if (isNumber(size)) return size === 0;
  const keys = Object.keys(value).length;
  return keys === 0;
};

export const enterSpaceKeys = event => {
  return event.code === 'Space' || event.key === ' ' || event.key === 'Enter';
};

export const uid = (length = 8) => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  const charsLength = chars.length;
  let result = '';
  for (let i = 0; i < length; i += 1) {
    result += chars.charAt(Math.floor(Math.random() * charsLength));
  }
  return result;
};

export { default as debounce } from 'underscore/modules/debounce.js';
export { default as throttle } from 'underscore/modules/throttle.js';
